import type { TypedSupabaseClient } from '../client';
import type {
  IProCustomerRepository,
  ProCustomer,
  RegisterProCustomerInput,
  UpdateProCustomerAdminInput,
  UpdateProCustomerInput,
} from '../../../interfaces/src';

export class SupabaseProCustomerRepository implements IProCustomerRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async getCurrent(): Promise<ProCustomer | null> {
    const { data: userData } = await this.supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId) return null;

    const { data, error } = await (this.supabase as any)
      .from('pro_customers')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data ? mapProCustomer(data) : null;
  }

  async createSelf(input: Omit<RegisterProCustomerInput, 'password'>): Promise<ProCustomer> {
    const { data: userData } = await this.supabase.auth.getUser();
    const userId = userData.user?.id;
    if (!userId) throw new Error('Not authenticated');

    const tenant = await resolveTenantBySubdomain(this.supabase, input.tenantSubdomain);
    if (!tenant) throw new Error(`Tenant introuvable: ${input.tenantSubdomain}`);

    const { data, error } = await (this.supabase as any)
      .from('pro_customers')
      .insert({
        user_id: userId,
        tenant_id: tenant.id,
        first_name: input.firstName,
        last_name: input.lastName,
        email: input.email,
        phone: input.phone ?? null,
        whatsapp: input.whatsapp ?? null,
        country_code: input.countryCode ?? '+216',
        registration_data: input.registrationData ?? null,
        status: 'pending',
      })
      .select()
      .single();
    if (error) throw error;
    return mapProCustomer(data);
  }

  async updateSelf(input: UpdateProCustomerInput): Promise<ProCustomer> {
    const current = await this.getCurrent();
    if (!current) throw new Error('Aucun profil pro');

    const patch: Record<string, unknown> = {};
    if (input.firstName !== undefined) patch['first_name'] = input.firstName;
    if (input.lastName !== undefined) patch['last_name'] = input.lastName;
    if (input.phone !== undefined) patch['phone'] = input.phone;
    if (input.whatsapp !== undefined) patch['whatsapp'] = input.whatsapp;
    if (input.countryCode !== undefined) patch['country_code'] = input.countryCode;
    if (input.profilePictureUrl !== undefined) patch['profile_picture_url'] = input.profilePictureUrl;

    const { data, error } = await (this.supabase as any)
      .from('pro_customers')
      .update(patch)
      .eq('id', current.id)
      .select()
      .single();
    if (error) throw error;
    return mapProCustomer(data);
  }

  async updateAdmin(id: string, input: UpdateProCustomerAdminInput): Promise<ProCustomer> {
    const patch: Record<string, unknown> = {};
    if (input.commissionRate !== undefined) {
      patch['commission_rate'] = Math.min(100, Math.max(0, input.commissionRate));
    }
    if (input.defaultRemiseTier !== undefined) {
      patch['default_remise_tier'] = input.defaultRemiseTier;
    }

    const { data, error } = await (this.supabase as any)
      .from('pro_customers')
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapProCustomer(data);
  }

  async listForTenant(filter?: { status?: ProCustomer['status'] }): Promise<ProCustomer[]> {
    let query = (this.supabase as any).from('pro_customers').select('*');
    if (filter?.status) query = query.eq('status', filter.status);
    const { data, error } = await query.order('created_at', { ascending: false });
    if (error) throw error;
    return ((data ?? []) as Array<Record<string, unknown>>).map(mapProCustomer);
  }

  async approve(id: string): Promise<ProCustomer> {
    const { data, error } = await (this.supabase as any).rpc('pro_customer_approve', { p_id: id });
    if (error) throw error;
    return mapProCustomer(data as Record<string, unknown>);
  }

  async reject(id: string, reason: string): Promise<ProCustomer> {
    const { data, error } = await (this.supabase as any).rpc('pro_customer_reject', {
      p_id: id,
      p_reason: reason,
    });
    if (error) throw error;
    return mapProCustomer(data as Record<string, unknown>);
  }
}

async function resolveTenantBySubdomain(
  client: TypedSupabaseClient,
  subdomain: string,
): Promise<{ id: string } | null> {
  const trimmed = subdomain.trim().toLowerCase();
  if (!trimmed) return null;
  const { data, error } = await (client as any)
    .from('tenants')
    .select('id')
    .eq('subdomain', trimmed)
    .maybeSingle();
  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data ? { id: data['id'] as string } : null;
}

function mapProCustomer(row: Record<string, unknown>): ProCustomer {
  return {
    id: row['id'] as string,
    tenantId: row['tenant_id'] as string,
    userId: row['user_id'] as string,
    firstName: row['first_name'] as string,
    lastName: row['last_name'] as string,
    email: row['email'] as string,
    phone: (row['phone'] as string) ?? null,
    whatsapp: (row['whatsapp'] as string) ?? null,
    countryCode: (row['country_code'] as string) ?? '+216',
    status: row['status'] as ProCustomer['status'],
    profilePictureUrl: (row['profile_picture_url'] as string) ?? null,
    registrationData: (row['registration_data'] as Record<string, unknown>) ?? null,
    commissionRate: Number(row['commission_rate'] ?? 0),
    defaultRemiseTier: parseRemiseTier(row['default_remise_tier']),
    approvedAt: (row['approved_at'] as string) ?? null,
    approvedBy: (row['approved_by'] as string) ?? null,
    rejectedReason: (row['rejected_reason'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function parseRemiseTier(value: unknown): ProCustomer['defaultRemiseTier'] {
  return value === 'min' || value === 'max' ? value : 'moy';
}
